import findIndexByInstanceKey from '../notifications/helpers/findIndexByInstanceKey';
import findIndexToInsertAt from '../notifications/helpers/findIndexToInsertAt';
import { ObservableMap } from 'mobx';
import { resetSelection, singleSelectRow } from 'owa-mail-actions';
import removeRowsFromListViewStore from 'owa-mail-actions/lib/triage/removeRowsFromListViewStore';
import { setRowOperation } from 'owa-mail-list-actions';
import { getFirstUnpinnedRowIndex } from 'owa-mail-list-response-processor';
import { RowOperation } from 'owa-mail-list-store';
import TableOperations from 'owa-mail-list-table-operations';
import tombstoneOperations, { TombstoneReasonType } from 'owa-mail-list-tombstone';
import { isNudgedRow } from 'owa-mail-nudge-store';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import { select } from 'satcheljs/lib/legacy';
import {
    listViewStore,
    TableQueryType,
    TableView,
    getRowIdsFromRowKeys,
    getRowKeysFromRowIds,
    MailRowDataPropertyGetter,
} from 'owa-mail-list-store';

export interface MarkRowsAsPinnedState {
    tableViews: ObservableMap<string, TableView>;
}

/**
 * Move the target row to the index after pin/unpin
 * @param lastDeliveryOrRenewTimeStamp the last delivery or renew time of the target row data
 * @param lastDeliveryTimeStamp the last delivery time of target row data
 * @param tableView the table view
 * @param targetRowKey the target row id
 * @param shouldMarkAsPinned whether this is a pin action, otherwise unpin
 * @returns flag indicating whether a position was found for the row or not.
 */
function moveUpdatedRowInList(
    lastDeliveryOrRenewTimeStamp: string,
    lastDeliveryTimeStamp: string,
    tableView: TableView,
    targetRowKey: string,
    shouldMarkAsPinned: boolean
): boolean {
    const oldIndex = findIndexByInstanceKey(targetRowKey, tableView);

    // Remove row from old position in the list
    if (oldIndex < 0) {
        throw new Error('Row being pinned / unpinnned must exist in the list');
    }

    // Index to start search at. If we're pinning, start from top, if unpinning, it can only be moved
    // to at or below the old index
    let indexToInsertAt;
    const startIndex = shouldMarkAsPinned ? 0 : oldIndex + 1;

    if (!shouldMarkAsPinned && isNudgedRow(targetRowKey, tableView.id)) {
        // find index for the unpinned nudged row
        indexToInsertAt = getFirstUnpinnedRowIndex(tableView, startIndex);
    } else {
        indexToInsertAt = findIndexToInsertAt(
            lastDeliveryOrRenewTimeStamp,
            lastDeliveryTimeStamp,
            tableView,
            startIndex,
            !shouldMarkAsPinned /*skipNudgedRows when unpinning a non-nudged row*/
        );
    }

    if (oldIndex < indexToInsertAt) {
        // In case of unpin, decrease 1 index because indexToInsertAt is calculated based on that row
        // is still pinned in the table
        indexToInsertAt--;
    }

    if (indexToInsertAt == -1) {
        // Remove the row if could not place it, because the row could have moved down (and out of loaded range) for any reason from another client,
        // e.g unpin or delete the latest item in the conversation from another client
        removeRowsFromListViewStore([targetRowKey], tableView);
        return false;
    }

    TableOperations.updateRowPosition(indexToInsertAt, targetRowKey, tableView);
    return true;
}

/**
 * Try select the next row when pin/unpin
 * @param rowKeys the row keys of the rows to pin/unpin
 * @param originalTableView the original table view
 */
function trySelectNextRowUponPinUnpin(rowKeys: string[], originalTableView: TableView) {
    const keyOfLastRowToPin = rowKeys[rowKeys.length - 1];
    if (!originalTableView.selectedRowKeys.has(keyOfLastRowToPin)) {
        // Do not update the selection if the pinned row is not selected
        return;
    }

    const indexOfLastRowToPin = originalTableView.rowKeys.indexOf(keyOfLastRowToPin);

    // Select the next row in table if the pinned row is selected and it's not the last row in table
    const shouldSelectNextRow = indexOfLastRowToPin < originalTableView.rowKeys.length - 1;
    if (shouldSelectNextRow) {
        singleSelectRow(
            originalTableView,
            originalTableView.rowKeys[indexOfLastRowToPin + 1],
            false /* isUserNavigation */,
            MailListItemSelectionSource.RowRemoval
        );
    } else {
        // Clear the selection if there is no next row to select, eg last row in table is selected and pinned
        resetSelection(originalTableView, MailListItemSelectionSource.Reset);
    }
}

/**
 * Mark the row pinned/unpinned in list view store
 * @param rowKeys the rowKeys to act upon
 * @param originalTableViewId the table view id
 * @param shouldMarkAsPinned whether this is a pin action, otherwise unpin
 * @param updateRenewTimeAction the delegate action which updates the row data's renew time
 */
export default select<MarkRowsAsPinnedState>({
    tableViews: () => listViewStore.tableViews,
})(function markRowsPinnedInListViewStore(
    rowKeys: string[],
    originalTableViewId: string,
    shouldMarkAsPinned: boolean,
    updateRenewTimeAction: (
        rowKey: string,
        tableViewId: string,
        shouldMarkAsPinned: boolean
    ) => void,
    state?: MarkRowsAsPinnedState
) {
    const originalTableView = state.tableViews.get(originalTableViewId);
    if (originalTableView.tableQuery.type === TableQueryType.Folder) {
        // Add the rowKeys to tombstone so they don't pop back in.
        // This needs to be done first as the store update for pin may remove the row from table.
        // Only need to do this for folder table, which supports notification and pin/delete actions
        tombstoneOperations.add(rowKeys, originalTableView.serverFolderId, TombstoneReasonType.Pin);

        // Select next row.
        // Skip this for other table, like search, as the row being pinned/unpinned never moves in the list
        trySelectNextRowUponPinUnpin(rowKeys, originalTableView);
    }

    const rowIdStrings = getRowIdsFromRowKeys(rowKeys, originalTableViewId);
    rowIdStrings.forEach(rowId => {
        state.tableViews.forEach((tableView: TableView) => {
            if (tableView.tableQuery.folderId != originalTableView.tableQuery.folderId) {
                return;
            }

            const rowKeys = getRowKeysFromRowIds([rowId], tableView);
            rowKeys.forEach(rowKey => {
                updateRenewTimeAction(rowKey, tableView.id, shouldMarkAsPinned);

                // Only move the pinned row to the top if it's in a mail table.
                // For search table, we don't move the row after it's pinned.
                if (tableView.tableQuery.type === TableQueryType.Folder) {
                    // Treat this pinning operation as a row move operation.
                    // This is used to restore list view scroll position.
                    // Make sure the state is used synchronously on the current thread,
                    // The row operation is reset once we let go of the thread
                    setRowOperation(tableView, RowOperation.RowMove);

                    const foundPositionForRowMove = moveUpdatedRowInList(
                        MailRowDataPropertyGetter.getLastDeliveryOrRenewTimeStamp(
                            rowKey,
                            tableView
                        ),
                        MailRowDataPropertyGetter.getLastDeliveryTimeStamp(rowKey, tableView),
                        tableView,
                        rowKey,
                        shouldMarkAsPinned
                    );

                    // We could not find position for this row, override the entry in the tombstone map with delete operation
                    // Currently we tombstone rows only for the table where the action was taken
                    if (!foundPositionForRowMove && tableView.id == originalTableViewId) {
                        tombstoneOperations.add(
                            [rowKey],
                            originalTableView.serverFolderId,
                            TombstoneReasonType.RowRemove
                        );
                    }
                }
            });
        });
    });
});
